import $ from 'jquery';
import _ from 'lodash';
import { fabric } from 'fabric';
import {
  saveEditorState,
  triggerStateChangedRequest,
  addEditorHistoryEntry,
  resetEditorState,
  editorUndo,
  editorRedo,
  addObjRefId,
  resetAllDynamicModes,
  hideAllMenus,
  editorAddText,
  toggleObjectEraser,
  selectErasingObject,
  b64toBlob,
} from '~core';
import { closeNavbarSubmenu } from '~core/navbar/submenus';
import { textFontSize } from './textFontSize';
import {
  onObjectImageSelected,
  addCanvasImage,
  refreshImageFilterPreviews,
} from '~src/image';
import { isCanvasDragSource } from '~core/image/isCanvasDragSource';
import { showSidebarTab } from '~src/sidebarLegacy';
import {
  onObjectTextSelected,
  showMenuText,
  onSelectTextEditing,
} from '~core/text';
import {
  showMenuMagicFrame,
  toggleMagicFrameEditMode,
  showMagicFrameCustomControls,
  hideMagicFrameCustomControls,
} from '~core/magicFrame';
import {
  isGroupSelection,
  changeToLock,
  changeToUnLock,
  lockObject,
  deleteObject,
  deselectObjects,
} from '~src/ui';
import {
  copyToClipboard,
  readFromClipboard,
  addCanvasObjectsFromJSON,
  isStringValidJSON,
  cleanBase64,
} from '~src/utils';
import { updateShadowValuesFromActiveObject } from '~src/sidebar/index';
import {
  markCurrentPageImageDirty,
  scheduleCurrentPageThumbnail,
} from '~src/multipages';
import { getState, modifyState } from '~src/state';
import isMobile from 'is-mobile';
import { handleSvgColors } from '~src/svg-colors';
import { v4 as uuidv4 } from 'uuid';
import { unsetState } from './state';
import {
  checkSpellingDebounced,
  removeSpellCheckPopup,
  hasSpellCheckErrors,
  handleTextboxRightClick,
} from '~core/spellcheck';
import { stopChangeClothes } from '~core/changeClothes';
import {
  handleRulerAfterRender,
  handleRulerObjectMoving,
  handleRulerObjectModified,
} from '~core/ruler';

function onTextSelectionChange() {
  const activeObject = getState('activeObject');
  const activeObjectType = getState('activeObjectType');

  if (activeObjectType === 'textbox') {
    showMenuText();
    onSelectTextEditing(activeObject);
  }
}

const onSvgGroupSelected = () => {
  hideAllMenus();
  $('#editor-menu-svg').show();
  $('#editor-menu-object').show();

  const selection = window.canvas.getActiveObject();

  handleSvgColors();

  // fix missing path ids
  let objects;

  if (selection.getObjects) {
    objects = selection.getObjects();
  } else {
    objects = [selection];
  }

  objects.map((object) => {
    // uuid length is 36
    if (!object.id || object.id.length !== 36) {
      object.id = uuidv4();
    }

    if (
      object.fill !== null &&
      typeof object.fill === 'object' &&
      object.fill.colorStops
    ) {
      object.fill.colorStops.map((colorStop) => {
        colorStop.id = uuidv4();
      });
    }

    if (
      object.stroke !== null &&
      typeof object.stroke === 'object' &&
      object.stroke.colorStops
    ) {
      object.stroke.colorStops.map((colorStop) => {
        colorStop.id = uuidv4();
      });
    }
  });
};

function onObjectDeselected() {
  modifyState('isDragging', false);
  modifyState('activeObjectType', false);
  resetAllDynamicModes();

  // Change Clothes V2 should be stopped when deselecting an image (if active)
  const isChangeClothesActive = getState('isChangeClothesActive');
  if (isChangeClothesActive) {
    stopChangeClothes();
  }

  if (getState('isErasing', false)) {
    return;
  }

  // TODO: remove when we implement the sidebar always open
  if (getState('ignoreNextObjectDeselect')) {
    modifyState('ignoreNextObjectDeselect', false);
    return;
  }

  resetEditorState();
}

function onObjectSelected() {
  hideMagicFrameCustomControls();
  const selection = window.canvas.getActiveObject();

  if (!selection) {
    throw new Error('No object selected');
  }

  $('#editor-menu-object').show();

  modifyState('activeObjectType', selection.type);
  modifyState('activeObject', selection);

  let cornerSizeValue = 60;
  let borderScaleFactorValue = 6;

  if (isMobile()) {
    if (window.canvas.width > window.canvas.height) {
      cornerSizeValue = window.canvas.width / 40;
      borderScaleFactorValue = window.canvas.width / 300;
    } else {
      cornerSizeValue = window.canvas.height / 40;
    }
  } else {
    if (window.canvas.width > window.canvas.height) {
      cornerSizeValue = window.canvas.width / 80;
      borderScaleFactorValue = window.canvas.width / 550;
    } else {
      cornerSizeValue = window.canvas.height / 80;
    }
  }

  selection.set({
    cornerColor: '#68A3BB',
    borderColor: '#d7d5d5',
    hasRotatingPoint: false,
    rotatingPointOffset: 150,
    borderScaleFactor: borderScaleFactorValue,
    cornerSize: cornerSizeValue, // pallini
    padding: 0,
    cornerStyle: 'circle',
  });

  if (isGroupSelection()) {
    // This block could go down inside the switch case for groups
    // but linting rules don't like declaring stuff inside the case block
    const objectsLocked = selection._objects.map((object) => {
      const isLocked = object.lockMovementY && object.lockMovementX;
      return isLocked;
    });
    const atLeastOneIsLocked = objectsLocked.includes(true);

    if (atLeastOneIsLocked) {
      lockObject(selection);
      // selection.borderColor = 'red';
      changeToUnLock();
    }
  } else {
    addObjRefId(selection);
  }

  switch (selection.type) {
    case 'textbox':
      onObjectTextSelected();
      if (selection.lockMovementY === true) {
        selection.borderColor = 'red';
        changeToUnLock();
      } else {
        changeToLock();
      }
      textFontSize();
      break;
    case 'i-text':
      onObjectTextSelected();
      break;

    case 'image':
      if (selection.lockMovementY === true) {
        selection.borderColor = 'red';
        changeToUnLock();
      } else {
        changeToLock();
      }
      onObjectImageSelected();
      break;

    case 'rect':
      onSvgGroupSelected();
      break;

    case 'path':
      onSvgGroupSelected();
      break;

    case 'group':
      onSvgGroupSelected();
      break;

    case 'MagicFrame':
      onObjectImageSelected();
      showMenuMagicFrame();
      showMagicFrameCustomControls();
      break;

    default:
      break;
  }

  updateShadowValuesFromActiveObject();
  window.canvas.renderAll();
}

const registerDoubleClickOnCanvas = () => {
  fabric.util.addListener(window.canvas.upperCanvasEl, 'dblclick', (event) => {
    const target = window.canvas.findTarget(event);

    if (target) {
      if (target.type === 'MagicFrame') {
        // toggleMagicFrameEditMode lavora sull'active object: senza questa
        // selezione un doppio click su una cornice non selezionata non fa nulla.
        if (window.canvas.getActiveObject() !== target) {
          window.canvas.setActiveObject(target);
        }
        toggleMagicFrameEditMode();
        if (window.canvas.getActiveObject()) {
          onObjectImageSelected();
        }
      } else if (typeof target._magicFrame !== 'undefined') {
        toggleMagicFrameEditMode();
      }
    }
  });
};

function afterRenderDispatcher() {
  // I render delle esportazioni non cambiano la pagina.
  if (getState('isExporting', false)) {
    return;
  }

  // L'immagine di stampa si rigenera solo quando serve (cambio pagina,
  // esportazioni); la miniatura della sidebar con un debounce.
  markCurrentPageImageDirty();
  scheduleCurrentPageThumbnail();
}

$('body').on('dragend', async (event) => {
  // Only photos from the Photos sidebar are draggable onto the canvas. Every
  // other on-page <img> (e.g. a SweetAlert2 preview, an AI tool icon) is
  // ignored so an accidental drag never adds it as a canvas image.
  if (isCanvasDragSource(event.target)) {
    const url = event.target.src;
    const image = await addCanvasImage(url);
    const mouse = window.canvas.getPointer(event);
    image.set({
      top: mouse.y - image.height / 2,
      left: mouse.x - image.width / 2,
    });
    window.canvas.renderAll();
  }
});

// Used to make dragend actually work
$('body').on('drop', (event) => {
  event.preventDefault();
});

function setCanvasEventListeners() {
  registerDoubleClickOnCanvas();

  // Prevent default context menu on textboxes with spell check errors
  window.canvas.upperCanvasEl.addEventListener('contextmenu', (e) => {
    // const pointer = window.canvas.getPointer(e);
    const target = window.canvas.findTarget(e, false);

    if (target && target.type === 'textbox' && hasSpellCheckErrors(target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  });

  window.canvas.on({
    'after:render': () => {
      afterRenderDispatcher();
      handleRulerAfterRender();
      // modifyState('afterRenderTs', Date.now());
    },
    'object:scaling': (event) => {
      // Handle ruler object scaling (if needed)
      handleRulerObjectModified(event);

      // Skip guidelines - they shouldn't trigger state changes
      if (event.target && event.target.isGuideline) {
        return;
      }

      // Note: Alignment and snapping are handled by AlignmentManager and SnapManager
    },
    'before:render': () => {
      // Check if popup exists before trying to remove it
      const popupExists = document.getElementById(
        'spellcheck-suggestions-popup'
      );
      if (!popupExists) {
        return;
      }

      // Check if we should remove the popup
      if (typeof window.canvas.getActiveObject === 'function') {
        const activeObject = window.canvas.getActiveObject();
        // Keep popup if:
        // 1. We have an active textbox with errors OR
        // 2. Popup was created recently (within 5 seconds) OR
        // 3. We're currently exporting (to avoid flicker during thumbnail generation) OR
        // 4. The popup is for a textbox that still has errors (even if not active)
        const shouldKeepPopup =
          (activeObject &&
            activeObject.type === 'textbox' &&
            hasSpellCheckErrors(activeObject)) ||
          (window._spellCheckPopupTimestamp &&
            Date.now() - window._spellCheckPopupTimestamp < 5000) ||
          getState('isExporting', false) ||
          (window._spellCheckPopupTextbox &&
            hasSpellCheckErrors(window._spellCheckPopupTextbox));

        if (!shouldKeepPopup) {
          removeSpellCheckPopup(true); // Force removal if conditions aren't met
        }
      }
    },
    'custom:state:changed': () => {
      addEditorHistoryEntry();
    },
    'object:modified': (event) => {
      // Handle ruler guideline modifications
      handleRulerObjectModified(event);

      // Skip guidelines - they shouldn't trigger state changes
      if (event.target && event.target.isGuideline) {
        return;
      }
      triggerStateChangedRequest();
    },
    'object:added': (event) => {
      if (_.has(event, 'target._magicFrame', false)) {
        return;
      }
      // Skip guidelines - they shouldn't trigger state changes
      if (event.target && event.target.isGuideline) {
        return;
      }
      // A questo punto la cornice magica non ha ancora clip né foto: il flusso
      // che la aggiunge creerà la history entry a frame:init completato.
      if (event.target && event.target.type === 'MagicFrame') {
        event.target.set('erasable', false);
        return;
      }
      triggerStateChangedRequest();
      event.target.set('erasable', false);
    },
    'editing:exited': () => {
      resetEditorState();
      triggerStateChangedRequest();
    },
    'selection:created': () => {
      onObjectSelected();
      removeSpellCheckPopup();
      // Enable download selection button
      $('#download-selection').prop('disabled', false);
    },
    'selection:updated': () => {
      onObjectSelected();
      removeSpellCheckPopup();
      // Enable download selection button
      $('#download-selection').prop('disabled', false);
    },
    'selection:cleared': () => {
      onObjectDeselected();
      window.canvas.renderAll();
      removeSpellCheckPopup();
      // Disable download selection button
      $('#download-selection').prop('disabled', true);
    },
    'text:editing:entered': () => {
      onObjectTextSelected();
    },
    'text:editing:exited': (e) => {
      resetEditorState();
      triggerStateChangedRequest();
      checkSpellingDebounced(e);
    },
    'text:editing:changed': (e) => {
      triggerStateChangedRequest();
      // TODO: checkSpellingDebounced is not working, also
      // we need to test if we want this event or not because it might be annoying
      // also, we need to verify if we accept a single correciton, the popuo should not disappear and trigger another spell check
      // as it causes an annoying flash.
      checkSpellingDebounced(e);
    },
    'text:changed': (e) => {
      triggerStateChangedRequest();
      // TODO: checkSpellingDebounced is not working, also
      // we need to test if we want this event or not because it might be annoying
      // also, we need to verify if we accept a single correciton, the popuo should not disappear and trigger another spell check
      // as it causes an annoying flash.
      checkSpellingDebounced(e);
    },
    'text:selection:changed': () => {
      onTextSelectionChange();
    },
    'mouse:up': function () {
      modifyState('isDragging', false);
      modifyState('selection', true);

      const draggedObject = getState('draggedObject');

      if (
        draggedObject &&
        draggedObject.type === 'image' &&
        getState('magicFrameWithPendingPersonDragUpdate')
      ) {
        window.canvas.remove(draggedObject);
        unsetState('magicFrameWithPendingPersonDragUpdate');
        unsetState('magicFrameWithPendingPersonDragPreviousData');
        unsetState('pauseMagicFrameDraggingCheck');
      }

      unsetState('draggedObject');
    },
    'erasing:start': function () {
      $('#editor-menu-stop-eraser').prop('disabled', false);
      $('#editor-menu-undo-eraser').prop('disabled', false);
    },
    'erasing:end': function (event) {
      selectErasingObject();
      refreshImageFilterPreviews([
        ...(event?.targets || []),
        ...(event?.subTargets || []),
      ]);
    },
    'mouse:down': function (event) {
      modifyState('isDragging', true);
      modifyState('draggedObject', event.target);

      // Handle right-click for spell check
      if (event.e.button === 2) {
        // Right mouse button
        // Find the object under the mouse cursor
        const targetObject = this.findTarget(event.e, false);

        // Check if we clicked on a textbox with spell check errors
        if (
          targetObject &&
          targetObject.type === 'textbox' &&
          hasSpellCheckErrors(targetObject)
        ) {
          // Handle the spell check right-click
          handleTextboxRightClick(event.e, targetObject);
        }
      }
    },
    'object:moving': async (event) => {
      closeNavbarSubmenu('export');

      // Handle ruler guideline movements
      handleRulerObjectMoving(event);

      if (event.target.type !== 'image') {
        return;
      }

      if (
        event.target.type === 'image' &&
        typeof event.target._magicFrame !== 'undefined'
      ) {
        return;
      }

      const objs = window.canvas.getObjects();
      const mouse = window.canvas.getPointer(event.e);

      // loop over ALL canvas objects
      for (let i = 0; i < objs.length; i++) {
        if (objs[i].type !== 'MagicFrame') {
          continue;
        }

        // the image is over magicframe
        if (objs[i].containsPoint(mouse)) {
          if (getState('pauseMagicFrameDraggingCheck')) {
            continue;
          }
          modifyState('itWasOverMagicFrame', true);

          const magicFrame = objs[i];

          modifyState('pauseMagicFrameDraggingCheck', true);

          const selection = event.target;
          const isErased = typeof selection.eraser !== 'undefined';

          let url;

          if (isErased) {
            const imageBase64 = cleanBase64(selection.toDataURL());
            const attachmentsUploader = getState('uploader.allegati');
            const blob = b64toBlob(imageBase64.base64, imageBase64.contentType);

            attachmentsUploader.addFile({
              type: blob.type,
              data: blob,
            });

            const responseUpload = await attachmentsUploader.upload();

            attachmentsUploader.cancelAll();

            url = `${process.env.EDGE_RESIZER}${
              new URL(responseUpload.successful[0].uploadURL).pathname
            }`;
          } else {
            url =
              selection.getSrc() ||
              selection._element.src1 ||
              selection._element.currentSrc;
          }

          const previousPersonSrc = magicFrame.urls.person;

          modifyState('magicFrameWithPendingPersonDragPreviousData', {
            src: previousPersonSrc,
            scaleX: magicFrame.editingImg.scaleX,
            scaleY: magicFrame.editingImg.scaleY,
          });

          magicFrame.updatePerson(url).then(() => {
            magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
            magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
            // scalePersonToWidth has to be called twice or it don't work

            modifyState('magicFrameWithPendingPersonDragUpdate', magicFrame);

            window.canvas.renderAll();
          });
        } else {
          modifyState('itWasOverMagicFrame', false);
          modifyState('pauseMagicFrameDraggingCheck', false);
        }
      }

      if (
        getState('itWasOverMagicFrame') === false &&
        getState('magicFrameWithPendingPersonDragUpdate')
      ) {
        const magicFrame = getState('magicFrameWithPendingPersonDragUpdate');
        const previousData = getState(
          'magicFrameWithPendingPersonDragPreviousData'
        );
        if (
          !previousData ||
          !previousData.src ||
          !magicFrame ||
          !magicFrame.editingImg
        ) {
          unsetState('magicFrameWithPendingPersonDragUpdate');
          unsetState('magicFrameWithPendingPersonDragPreviousData');
          unsetState('pauseMagicFrameDraggingCheck');
          return;
        }

        const { src, scaleX, scaleY } = previousData;
        magicFrame.updatePerson(src).then(() => {
          magicFrame.editingImg.set('scaleX', scaleX);
          magicFrame.editingImg.set('scaleY', scaleY);
          magicFrame.toggleEditMode(); // do not change, it's magic
          magicFrame.toggleEditMode(); // do not change, it's magic
          unsetState('magicFrameWithPendingPersonDragUpdate');
          unsetState('magicFrameWithPendingPersonDragPreviousData');
          window.canvas.renderAll();
        });
      }
    },
  });
}

function startKeyboardListener() {
  // return; // todo: fix quando sidebar e' aperta
  window.onbeforeunload = function (e) {
    // Cancel the event
    e.preventDefault();

    // Chrome requires returnValue to be set
    e.returnValue = 'Sicuro di voler uscire?'; // TODO: inutile, credo
  };

  const ignoreFieldSelectors = [
    '#editor-font-size-input',
    '#input-filter-fonts',
    '#editor-sidebar-af-family-email',
    '#editor-sidebar-af-family-whatsapp',
    '#editor-sidebar-af-select-ricerca-comuni',
    '#editor-text-spacing-v-value',
    '#editor-text-spacing-h-value',
    '#editor-text-border-thickness-value',
  ];

  document.onkeyup = (e) => {
    if (getState('stopKeyboardListener')) {
      return;
    }

    const activeObject = getState('activeObject');
    if (activeObject && activeObject.isEditing) {
      return;
    }

    // Check if any ignored field is focused
    if (ignoreFieldSelectors.some((selector) => $(selector).is(':focus'))) {
      return;
    }

    switch (e.keyCode) {
      case 91 /* ctrl / cmd */:
        modifyState('keyboardCtrlDown', false);
        break;
      case 224 /* ctrl / cmd */:
        modifyState('keyboardCtrlDown', false);
        break;
      case 16 /* shift */:
        modifyState('keyboardShiftDown', false);
        break;
    }
  };

  document.onkeydown = async (e) => {
    if (getState('stopKeyboardListener')) {
      return;
    }

    // Check if any ignored field is focused
    if (ignoreFieldSelectors.some((selector) => $(selector).is(':focus'))) {
      return;
    }

    const activeObject = getState('activeObject');

    if (activeObject && activeObject.isEditing) {
      return;
    }

    const moveStep = getState('keyboardShiftDown') ? 150 : 25;

    switch (e.keyCode) {
      /**
       * Up Arrow
       */
      case 38:
        if (window.canvas.getActiveObject()) {
          window.canvas.getActiveObject().top -= moveStep;
          window.canvas.renderAll();
          triggerStateChangedRequest();
        }
        break;

      /**
       * Down Arrow
       */
      case 40:
        if (window.canvas.getActiveObject()) {
          window.canvas.getActiveObject().top += moveStep;
          window.canvas.renderAll();
          triggerStateChangedRequest();
        }
        break;

      /**
       * Left Arrow
       */
      case 37:
        if (window.canvas.getActiveObject()) {
          window.canvas.getActiveObject().left -= moveStep;
          window.canvas.renderAll();
          triggerStateChangedRequest();
        }
        break;

      /**
       * Right Arrow
       */
      case 39:
        if (window.canvas.getActiveObject()) {
          window.canvas.getActiveObject().left += moveStep;
          window.canvas.renderAll();
          triggerStateChangedRequest();
        }
        break;

      /**
       * Delete
       */
      case 46:
        deleteObject();
        break;

      /**
       * Backspace
       */
      case 8:
        deleteObject();
        break;

      /**
       * S
       */
      case 83:
        if (getState('keyboardCtrlDown')) {
          await saveEditorState(true, false);
          e.preventDefault();
          e.stopPropagation();
        }
        break;

      /**
       * Ctrl+CMD
       */
      case 91:
        modifyState('keyboardCtrlDown', true);
        break;

      /**
       * Ctrl+CMD
       */
      case 17:
        modifyState('keyboardCtrlDown', true);
        break;

      /**
       * Shift
       */
      case 16:
        modifyState('keyboardShiftDown', true);
        break;

      /**
       * Z
       */
      case 90:
        if (getState('keyboardShiftDown') && getState('keyboardCtrlDown')) {
          editorRedo();
        } else if (getState('keyboardCtrlDown')) {
          editorUndo();
        }
        break;

      /**
       * C
       */
      case 67:
        if (getState('keyboardCtrlDown')) {
          const activeObjectsJSON = {
            objects: [],
            canvasId: window.editorLoader.canvasId,
          };
          for (const activeObject of window.canvas.getActiveObjects()) {
            if (activeObject._magicFrame) {
              // we need to skip the child image of MagicFrame, we just need to copy the parent MagicFrame
              continue;
            }
            activeObjectsJSON.objects.push(activeObject.toJSON());

            const isPartOfGroup = isGroupSelection();

            if (isPartOfGroup) {
              const index = activeObjectsJSON.objects.length - 1;

              activeObjectsJSON.objects[index].left =
                activeObject.left +
                activeObject.group.left +
                activeObject.group.width / 2;

              activeObjectsJSON.objects[index].top =
                activeObject.top +
                activeObject.group.top +
                activeObject.group.height / 2;
            }
          }

          copyToClipboard(activeObjectsJSON);
        }
        break;

      /**
       * V
       */
      case 86:
        if (getState('isFocusingOnInput')) {
          return;
        }

        if (getState('keyboardCtrlDown')) {
          const clipboardContent = await readFromClipboard();
          if (isStringValidJSON(clipboardContent)) {
            const jsonObjects = JSON.parse(clipboardContent);
            addCanvasObjectsFromJSON(jsonObjects);
          } else {
            await editorAddText('BODY', clipboardContent);
          }
        }
        break;

      /**
       * X
       */
      case 88:
        if (getState('keyboardCtrlDown')) {
          const activeObjectsJSON = {
            objects: [],
            canvasId: window.editorLoader.canvasId,
          };
          for (const activeObject of window.canvas.getActiveObjects()) {
            activeObjectsJSON.objects.push(activeObject.toJSON());
            window.canvas.remove(activeObject);
          }
          copyToClipboard(activeObjectsJSON);
          window.canvas.renderAll();
        }
        break;

      /**
       * Escape
       */
      case 27:
        if (getState('isErasing', false)) {
          toggleObjectEraser();
          showSidebarTab('edit-images-ai');
          break;
        }

        deselectObjects();
        break;

      default:
        break;
    }
  };
}

export { startKeyboardListener, setCanvasEventListeners };
